import { useState } from 'react';
import Page from '@/components/Page';
import { updateUser } from '@/data/features/user/user';
import { useDispatch } from 'react-redux';
import {
  Anchor,
  Button,
  Checkbox,
  Text,
  TextInput,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useForm } from '@mantine/form';
import validate from '@/helpers/validations';
import { getMutationResult } from '@/helpers/query';
import { useLoginMutation } from '@/services/api';
import { updateOffer } from '@/data/features/offer/offer';
import { useNavigate, useParams } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { IconArrowNarrowLeft } from '@tabler/icons-react';
import routes from '@/routes';
import LoginImage from '@/assets/images/loginImage.svg?react'; // eslint-disable-line import/no-unresolved
import classes from './login.module.css';

const Login = () => {
  const { t } = useTranslation();
  const [apiLogin] = useLoginMutation();
  const { proposalNumber } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false); // handle loading on continue button
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const required = t('globals.validations.required');
  const uid = t('globals.validations.uid');
  const mobile = t('globals.validations.phone');

  const formValidations = {
    mobileNumber: (value) => validate(value, { required, mobile }),
    uid: (value) => validate(value, { required, uid }),
  };

  const initialValues = {
    mobileNumber: '',
    uid: '',
  };

  const form = useForm({
    mode: 'uncontrolled',
    initialValues,
    validate: formValidations,
  });

  const { onSubmit } = form;

  const handleSubmit = async (values) => {
    setLoading(true);

    const loginData = {
      ...values,
      proposalNumber,
    };

    const { data, isError } = await getMutationResult(loginData, apiLogin);

    if (!isError) {
      const {
        email,
        phoneNumber,
        fullName,
        idNumber,
      } = data.data;

      dispatch(updateUser({
        email,
        phoneNumber,
        fullName,
        idNumber,
      }));
      dispatch(updateOffer({
        id: proposalNumber,
      }));

      navigate(routes.otp.path);
    } else {
      setLoading(false);

      notifications.show({
        title: t('globals.error.title'),
        message: t('globals.error.message'),
      });
    }
  };

  const side = (
    <div className={classes.sideImage}>
      <LoginImage />
    </div>
  );
  const actions = <Button
    onClick={onSubmit((values) => handleSubmit(values))}
    disabled={!acceptedTerms || loading}
    rightSection={<IconArrowNarrowLeft size={19} />}
    loading={loading}
  >
    {t('pages.login.action')}
  </Button>;

  return (
    <Page
      side={side}
      actions={actions}
      title={t('pages.login.title')}
      subTitle={t('pages.login.subTitle')}
    >
      <form>
        <div className={classes.login}>
          <TextInput
            label={t('pages.login.inputs.mobile')}
            key={form.key('mobileNumber')}
            maxLength={10}
            {...form.getInputProps('mobileNumber')}
          />

          <TextInput
            label={t('pages.login.inputs.uid')}
            key={form.key('uid')}
            {...form.getInputProps('uid')}
          />
          <Checkbox
            classNames={{
              body: classes.checkBoxBody,
            }}
            label={
              <Text size="sm" c="#6b7280">
                אני מאשר/ת את{' '}
                <Anchor
                  href="https://www.creditpro.co.il/%D7%AA%D7%A0%D7%90%D7%99-%D7%A9%D7%99%D7%9E%D7%95%D7%A9-%D7%91%D7%90%D7%AA%D7%A8"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  תנאי השימוש
                </Anchor>
                {' '}ו
                <Anchor
                  href="https://www.creditpro.co.il/%D7%9E%D7%93%D7%99%D7%A0%D7%99%D7%95%D7%AA-%D7%94%D7%A4%D7%A8%D7%98%D7%99%D7%95%D7%AA"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  מדיניות הפרטיות
                </Anchor>
              </Text>
            }
            checked={acceptedTerms}
            onChange={(e) => setAcceptedTerms(e.currentTarget.checked)}
          >
          </Checkbox>
        </div>
      </form>
      {/* Footer */}
      <footer className={classes.footer}>
        <a
          href="https://www.creditpro.co.il/%D7%9E%D7%93%D7%99%D7%A0%D7%99%D7%95%D7%AA-%D7%94%D7%A4%D7%A8%D7%98%D7%99%D7%95%D7%AA"
          target="_blank"
          rel="noopener noreferrer"
        >
          <Text>
            {t('globals.privacyPolicy')}
          </Text>
        </a>
        <Text> | </Text>
        <a
          href="https://www.creditpro.co.il/%D7%AA%D7%A0%D7%90%D7%99-%D7%A9%D7%99%D7%9E%D7%95%D7%A9-%D7%91%D7%90%D7%AA%D7%A8"
          target="_blank"
          rel="noopener noreferrer"
        >
          <Text>
            {t('globals.termsOfUse')}
          </Text>
        </a>
      </footer>

    </Page>
  );
};

export default Login;
